import { css, cva } from '~/styled-system/css'

import type { FC } from 'react'

export type PaginationProps = {
  length: number
  index: number
}

const dot = cva({
  base: {
    width: '[8px]',
    height: '[8px]',
    minWidth: '[8px]',
    minHeight: '[8px]',
    rounded: 'full',
    bgColor: 'white',
    opacity: '[1]',
  },
  variants: {
    active: {
      false: {
        width: '[6px]',
        height: '[6px]',
        minWidth: '[6px]',
        minHeight: '[6px]',
        opacity: '60',
      },
    },
  },
  defaultVariants: {
    active: false,
  },
})

export const DotsPagination: FC<PaginationProps & { dynamic?: boolean }> =
  import.meta.env.VITE_MODE === 'ppg' ? Empty : IkyuDotsPagination

function Empty() {
  return null
}

function IkyuDotsPagination(props: PaginationProps & { dynamic?: boolean }) {
  if (props.dynamic) {
    return <DynamicDotsPagination {...props} />
  }
  return <StaticDotsPagination {...props} />
}

function StaticDotsPagination({ length, index }: PaginationProps) {
  return (
    <div
      className={css({
        position: 'absolute',
        bottom: '2',
        left: '[50%]',
        transform: 'translate(-50%, -50%)',
      })}
    >
      <ul className={css({ display: 'flex', gap: '1', transition: 'all', alignItems: 'center' })}>
        {Array.from({ length }).map((_, i) => {
          const active = i === index
          return <li key={`dot-${i}-${active}`} className={dot({ active })}></li>
        })}
      </ul>
    </div>
  )
}

const MAX_DOT_COUNT = 5
const ACTIVE_DOT_WIDTH = 8
const INACTIVE_DOT_WIDTH = 6
const DOT_GAP_WIDTH = 4
function calcSlideProgress(currentIndex: number, total: number, visibleDotsCount: number) {
  if (MAX_DOT_COUNT > total) {
    return 0
  }
  const fixedDotsCount = Math.ceil(visibleDotsCount / 2)
  const index = currentIndex + 1
  let position = 0
  if (index > total - fixedDotsCount) {
    position = total - visibleDotsCount
  } else if (index > fixedDotsCount) {
    position = index - fixedDotsCount
  }
  return -(INACTIVE_DOT_WIDTH + DOT_GAP_WIDTH) * position
}

function DynamicDotsPagination({ length, index }: PaginationProps) {
  const visibleDotsCount = Math.min(length, MAX_DOT_COUNT)
  const dotsWidth =
    ACTIVE_DOT_WIDTH +
    INACTIVE_DOT_WIDTH * (visibleDotsCount - 1) +
    DOT_GAP_WIDTH * (visibleDotsCount - 1)
  const slideProgress = calcSlideProgress(index, length, visibleDotsCount)
  return (
    <div
      className={css({
        position: 'absolute',
        bottom: '2',
        overflowX: 'hidden',
        left: '[50%]',
        transform: 'translate(-50%, -50%)',
      })}
      style={{ maxWidth: dotsWidth }}
    >
      <ul
        className={css({ display: 'flex', gap: '1', transition: 'all', alignItems: 'center' })}
        style={{ transform: `translateX(${slideProgress}px)` }}
      >
        {Array.from({ length }).map((_, i) => {
          const active = i === index
          return <li key={`dot-${i}-${active}`} className={dot({ active })}></li>
        })}
      </ul>
    </div>
  )
}

export function FractionPagination({ length, index }: PaginationProps) {
  return (
    <div
      className={css({
        position: 'absolute',
        top: '3',
        right: '3',
        py: '1',
        px: '2',
        borderRadius: 'full',
        color: 'white',
        fontSize: '[10px]',
        fontWeight: '400',
        lineHeight: '[12.5px]',
        backgroundColor: 'image.background',
      })}
    >
      {index + 1} / {length}
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  describe('DynamicDotsPagination の表示位置を計算する', () => {
    it.each([
      [0, 1, 1, 0],
      [0, 5, 5, 0],
      [4, 5, 5, 0],
      [0, 10, 5, 0],
      [2, 10, 5, 0],
      [3, 10, 5, -10],
      [6, 10, 5, -40],
      [7, 10, 5, -50],
      [9, 10, 5, -50],
    ])(
      'currentIndex %s, count %s, visibleDotsCount %s => slideProgress=%s',
      (currentIndex, count, visibleDotsCount, expected) => {
        expect(calcSlideProgress(currentIndex, count, visibleDotsCount) === expected).toBe(true)
      }
    )
  })
}
